import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api, ErroreApi, type VocePersona } from '../servizi/api.ts'
import { useAree, useCatalogo, useSessione } from '../servizi/sessione.tsx'
import { Attesa, Avviso, Intestazione, Vuoto } from '../componenti/Pezzi.tsx'
import { Bottone, Campo, Etichetta, Paginazione, Riquadro, Scelta, Tabella } from '../componenti/base.tsx'
import { IconaLucchetto, IconaPiu } from '../componenti/Icone.tsx'
import { SceltaComune, type ComuneScelto } from './Ente.tsx'
import { SceltaEnte, type Ente } from './Registro.tsx'
import { STATI_SCHEDA, etichettaStatoScheda } from '../servizi/dati-mancanti.ts'
import { opzioniSedi } from '../servizi/sedi.ts'
import { ImportaAnonimizzate } from './ImportaAnonimizzate.tsx'

type Risultato = { totale: number; risultati: VocePersona[] }
type ProgettoScelto = { id: string; titolo: string; codice_cidis: string | null }

/** Righe per pagina. La rotta /persone non ne accetta piu' di cento. */
const LIMITE = 50

/**
 * Filtri della ricerca, conservati negli argomenti dell'indirizzo. Oltre a testo, sede e stato, i
 * collegamenti della persona della STRUTTURA del 14/09 (ANA-55, ANA-57, ANA-60) che GET /persone
 * accetta: area e tipologia dai percorsi (D-05), progetto (D-10), tipo di persona (D-07), comune di
 * residenza o domicilio (D-20), fascia d'eta', categoria e vulnerabilita' (D-21), follow up, area
 * riservata, ente (D-22). Il perimetro di ogni filtro lo decide l'API.
 */
const FILTRI = [
  'cerca', 'telefono', 'sede', 'stato', 'area', 'tipologia', 'progetto', 'tipo', 'comune', 'fascia',
  'categoria', 'vulnerabilita', 'follow_up', 'area_riservata', 'stakeholder',
  // 460: schede a cui mancano dati obbligatori, da verificare, senza Sede Servizio.
  'da_completare', 'da_verificare', 'senza_sede',
  // 481: schede con dati anonimizzati.
  'anonimizzate',
] as const
/** Filtri che si azzerano con «Azzera i filtri»: tutti tranne il testo, che ha il suo campo. */
const COLLEGAMENTI = FILTRI.filter((f) => f !== 'cerca' && f !== 'telefono')
/** Filtri della seconda griglia, quelli che sul telefono stanno sotto «Altri filtri». */
const SECONDARI = COLLEGAMENTI.filter((f) => f !== 'sede' && f !== 'stato' && f !== 'area')

/**
 * Lunghezza minima del codice cercato: la ricerca e' per codice esatto, e con meno caratteri non
 * c'e' niente da trovare.
 */
const CODICE_MINIMO = 4

/** Stati della scheda, nell'ordine in cui compaiono nel filtro, con le parole di dati-mancanti.ts (460). */
const OPZIONI_STATO = ['completa', 'bozza', 'storico_incompleto', 'archiviata']
  .map((valore) => ({ valore, etichetta: STATI_SCHEDA[valore].etichetta }))

const SI_NO = [{ valore: 'true', etichetta: 'Sì' }, { valore: 'false', etichetta: 'No' }]

/** Permessi con cui GET /progetti/selezionabili risponde. */
const PERMESSI_PROGETTI = ['progetto.leggi', 'intervento.crea', 'servizio_erogato.registra', 'servizio_erogato.modifica']

/** Ricerca dei beneficiari. Chi puo' vedere solo le schede mascherate riceve le iniziali. */
export function Persone() {
  const { puo, sedi, tassonomia } = useSessione()
  const aree = useAree()
  const tipiPersona = useCatalogo('tipo_persona')
  const fasce = useCatalogo('fascia_eta')
  const categorie = useCatalogo('categoria_persona')
  const vulnerabilita = useCatalogo('vulnerabilita')
  const [parametri, setParametri] = useSearchParams()
  const [testo, setTesto] = useState(parametri.get('cerca') ?? '')
  // 460: codice fiscale o altro codice (STP, ENI, CUI...). E' un dato identificativo: resta nello
  // stato della pagina, non nell'indirizzo, e viaggia nel corpo di POST /persone/cerca, non nella
  // query string che il server registra nel log.
  const [codice, setCodice] = useState('')
  const [codiceInUso, setCodiceInUso] = useState('')
  const [esito, setEsito] = useState<Risultato | null>(null)
  const [caricamento, setCaricamento] = useState(true)
  const [errore, setErrore] = useState<string | null>(null)
  const [progetti, setProgetti] = useState<ProgettoScelto[]>([])
  const [comune, setComune] = useState<ComuneScelto | null>(null)
  const [ente, setEnte] = useState<Ente | null>(null)
  // Sul telefono i filtri secondari stanno chiusi finche' non si chiedono (stile-telefono.css); sul
  // computer il comando non si vede e restano aperti. Si aprono da soli se uno e' gia' in uso.
  const [altriFiltri, setAltriFiltri] = useState(() => SECONDARI.some((chiave) => parametri.get(chiave)))
  const annulla = useRef<AbortController | null>(null)
  const campoRicerca = useRef<HTMLInputElement>(null)

  const filtro = (chiave: (typeof FILTRI)[number]) => parametri.get(chiave) ?? ''

  // Posizione della finestra. Riparte da zero a ogni cambio di filtro o di ricerca: la pagina
  // calcolata sui filtri precedenti non corrisponde piu' ad alcun risultato. L'assestamento
  // avviene in fase di render, quindi la ricerca parte una volta sola e con la finestra nuova.
  const [salta, setSalta] = useState(0)
  // I valori sono serializzati e non concatenati: un filtro che contiene il separatore
  // darebbe la stessa chiave a combinazioni diverse, e la finestra non ripartirebbe.
  const chiaveFiltri = JSON.stringify([...FILTRI.map((c) => parametri.get(c)), codiceInUso])
  const [filtriInUso, setFiltriInUso] = useState(chiaveFiltri)
  // «Importa schede anonimizzate» (31/10/2026): la finestra e, dopo la conferma, la ricerca da rifare.
  const [importa, setImporta] = useState(false)
  const [importate, setImportate] = useState(0)
  if (chiaveFiltri !== filtriInUso) {
    setFiltriInUso(chiaveFiltri)
    setSalta(0)
  }

  // Il fuoco iniziale sta sul campo di ricerca, come prima della conversione ai componenti.
  useEffect(() => { campoRicerca.current?.focus() }, [])

  useEffect(() => {
    annulla.current?.abort()
    const controllore = new AbortController()
    annulla.current = controllore
    setCaricamento(true)
    const corpo: Record<string, string | number> = { limite: LIMITE, salta }
    for (const chiave of FILTRI) {
      const v = parametri.get(chiave)
      if (v) corpo[chiave] = v
    }
    if (codiceInUso) corpo.cf = codiceInUso
    api.post<Risultato>('/persone/cerca', corpo, { segnale: controllore.signal })
      .then((r) => { setEsito(r); setErrore(null) })
      .catch((e) => {
        if (e.name !== 'AbortError') {
          setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')
        }
      })
      .finally(() => { if (!controllore.signal.aborted) setCaricamento(false) })
    return () => controllore.abort()
  }, [parametri, salta, codiceInUso, importate])

  // Il codice si cerca dopo la stessa pausa, e solo quando e' abbastanza lungo da essere un codice.
  useEffect(() => {
    const t = setTimeout(() => {
      const pulito = codice.trim()
      const nuovo = pulito.length >= CODICE_MINIMO ? pulito : ''
      if (nuovo !== codiceInUso) setCodiceInUso(nuovo)
    }, 500)
    return () => clearTimeout(t)
  }, [codice]) // eslint-disable-line react-hooks/exhaustive-deps

  // Si cerca mentre si scrive, ma senza tempestare l'API: mezzo secondo di pausa.
  useEffect(() => {
    const t = setTimeout(() => {
      if (testo !== (parametri.get('cerca') ?? '')) aggiornaFiltri({ cerca: testo })
    }, 500)
    return () => clearTimeout(t)
  }, [testo]) // eslint-disable-line react-hooks/exhaustive-deps

  // Progetti della tendina: quelli a cui una registrazione si puo' legare, come nel registro.
  const leggeProgetti = PERMESSI_PROGETTI.some((p) => puo(p))
  useEffect(() => {
    if (!leggeProgetti) return
    let vivo = true
    api.get<ProgettoScelto[]>('/progetti/selezionabili?limite=500')
      .then((r) => { if (vivo) setProgetti(r) })
      .catch(() => { if (vivo) setProgetti([]) })
    return () => { vivo = false }
  }, [leggeProgetti])

  // Comune ed ente arrivano dall'indirizzo come codici: il nome si recupera una volta.
  const comuneFiltrato = parametri.get('comune')
  useEffect(() => {
    if (!comuneFiltrato) { setComune(null); return }
    if (comune?.codice_istat === comuneFiltrato) return
    const ripiego = { codice_istat: comuneFiltrato, nome: comuneFiltrato, provincia_sigla: null }
    api.get<ComuneScelto[]>(`/riferimenti/comuni?cerca=${encodeURIComponent(comuneFiltrato)}&limite=5`)
      .then((r) => setComune(r.find((c) => c.codice_istat === comuneFiltrato) ?? ripiego))
      .catch(() => setComune(ripiego))
  }, [comuneFiltrato]) // eslint-disable-line react-hooks/exhaustive-deps
  const enteFiltrato = parametri.get('stakeholder')
  useEffect(() => {
    if (!enteFiltrato) { setEnte(null); return }
    if (ente?.id === enteFiltrato) return
    const ripiego = { id: enteFiltrato, nome: 'Ente selezionato', sigla: null }
    if (!puo('ente.leggi')) { setEnte(ripiego); return }
    api.get<Ente>(`/enti/${encodeURIComponent(enteFiltrato)}`)
      .then((e) => setEnte({ id: e.id, nome: e.nome, sigla: e.sigla }))
      .catch(() => setEnte(ripiego))
  }, [enteFiltrato]) // eslint-disable-line react-hooks/exhaustive-deps

  function aggiornaFiltri(valori: Partial<Record<(typeof FILTRI)[number], string>>) {
    const nuovi = new URLSearchParams(parametri)
    for (const [chiave, valore] of Object.entries(valori)) {
      if (valore) nuovi.set(chiave, valore); else nuovi.delete(chiave)
    }
    setParametri(nuovi, { replace: true })
  }

  function azzera() {
    const nuovi = new URLSearchParams(parametri)
    for (const chiave of COLLEGAMENTI) nuovi.delete(chiave)
    setParametri(nuovi, { replace: true })
  }

  const tipologieInUso = (tassonomia?.tipologie ?? []).filter((t) => t.attiva || t.codice === filtro('tipologia'))
  const areaScelta = filtro('area')
  const conCollegamenti = COLLEGAMENTI.some((chiave) => parametri.get(chiave))
  const secondariInUso = SECONDARI.filter((chiave) => parametri.get(chiave)).length

  const nuovoBeneficiario = puo('persona.crea')
    ? <Link className="bottone bottone-primario" to="/persone/nuova"><IconaPiu /> Nuova scheda utente</Link>
    : undefined
  const importaAnonimizzate = puo('persona.crea')
    ? <Bottone variante="secondario" onClick={() => setImporta(true)}>Importa schede anonimizzate</Bottone>
    : undefined

  return (
    <>
      <Intestazione
        titolo="Beneficiari"
        sottotitolo={esito
          ? `${esito.totale === 1 ? '1 beneficiario' : `${esito.totale} beneficiari`} che puoi vedere`
          : undefined}
        azioni={nuovoBeneficiario && <>{importaAnonimizzate}{nuovoBeneficiario}</>}
      />

      <Riquadro azioni={conCollegamenti
        ? <Bottone variante="discreto" dimensione="piccolo" onClick={azzera}>Azzera i filtri</Bottone>
        : undefined}>
        <div className="griglia-filtri persona-filtri">
          <Campo etichetta="Cerca" tipo="search" valore={testo} cambia={setTesto}
                 riferimento={campoRicerca}
                 segnaposto="Nome, cognome, numero di scheda o codice dell’archivio storico" />
          {/* D-05: la tendina Area filtra gli elenchi. Cambiando area la tipologia scelta resta solo se
              appartiene all'area. */}
          <Scelta etichetta="Area" valore={areaScelta} vuota="Tutte"
                  cambia={(v) => {
                    const tipologia = tipologieInUso.find((t) => t.codice === filtro('tipologia'))
                    aggiornaFiltri({ area: v, tipologia: tipologia && (!v || tipologia.area_codice === v) ? tipologia.codice : '' })
                  }}
                  opzioni={aree.aree.filter((a) => a.attiva || a.codice === areaScelta)
                    .map((a) => ({ valore: a.codice, etichetta: a.nome }))} />
          <Scelta etichetta="Sede" valore={filtro('sede')} cambia={(v) => aggiornaFiltri({ sede: v })}
                  vuota="Tutte" opzioni={opzioniSedi(sedi)} />
          <Scelta etichetta="Stato" valore={filtro('stato')} cambia={(v) => aggiornaFiltri({ stato: v })}
                  vuota="Tutti" opzioni={OPZIONI_STATO} />
          {/* Per codice cerca solo chi vede i codici in chiaro (Osservazioni 4 del 23/10/2026): per gli
              altri l'API non trova nessuno, e il campo non si offre. */}
          {puo('persona.identificativi') && (
            <Campo etichetta="Codice fiscale o altro codice" tipo="search" valore={codice} cambia={setCodice}
                   autoComplete="off" maxLength={30} segnaposto="CF, STP, ENI, CUI…"
                   aiuto="Si cerca il codice intero: spazi, trattini e maiuscole non contano. Non resta nell’indirizzo della pagina." />
          )}
        </div>

        <div className="persona-altri-filtri">
          <Bottone variante="secondario" larghezzaPiena aria-expanded={altriFiltri}
                   aria-controls="persona-filtri-secondari" onClick={() => setAltriFiltri((v) => !v)}>
            {altriFiltri ? 'Meno filtri' : 'Altri filtri'}
            {secondariInUso > 0 && ` (${secondariInUso} in uso)`}
          </Bottone>
        </div>
        <div id="persona-filtri-secondari"
             className={`filtri-report persona-collegamenti${altriFiltri ? '' : ' persona-collegamenti-chiusi'}`}>
          <Scelta etichetta="Tipologia" valore={filtro('tipologia')} vuota="Tutte"
                  cambia={(v) => aggiornaFiltri({ tipologia: v })}
                  opzioni={tipologieInUso.filter((t) => !areaScelta || t.area_codice === areaScelta)
                    .map((t) => ({ valore: t.codice, etichetta: t.nome }))} />
          {leggeProgetti && (
            <Scelta etichetta="Progetto" valore={filtro('progetto')} vuota="Tutti"
                    cambia={(v) => aggiornaFiltri({ progetto: v })}
                    opzioni={progetti.map((p) => ({
                      valore: p.id, etichetta: p.codice_cidis ? `${p.codice_cidis} · ${p.titolo}` : p.titolo }))} />
          )}
          <Scelta etichetta="Profilo utenti" valore={filtro('tipo')} vuota="Tutti"
                  cambia={(v) => aggiornaFiltri({ tipo: v })}
                  opzioni={tipiPersona.voci.map((t) => ({ valore: t.codice, etichetta: t.nome }))} />
          <SceltaComune etichetta="Comune di residenza o domicilio" vuota="Tutti i comuni" valore={comune}
                        cambia={(c) => { setComune(c); aggiornaFiltri({ comune: c?.codice_istat ?? '' }) }} />
          <Scelta etichetta="Fascia d’età" valore={filtro('fascia')} vuota="Tutte"
                  cambia={(v) => aggiornaFiltri({ fascia: v })}
                  opzioni={fasce.voci.map((f) => ({ valore: f.codice, etichetta: f.nome }))} />
          <Scelta etichetta="Categoria di utenti" valore={filtro('categoria')} vuota="Tutte"
                  cambia={(v) => aggiornaFiltri({ categoria: v })}
                  opzioni={categorie.voci.map((c) => ({ valore: c.codice, etichetta: c.nome }))} />
          {puo('persona.leggi_particolari') && (
            <Scelta etichetta="Vulnerabilità" valore={filtro('vulnerabilita')} vuota="Tutte"
                    cambia={(v) => aggiornaFiltri({ vulnerabilita: v })}
                    opzioni={vulnerabilita.voci.map((x) => ({ valore: x.codice, etichetta: x.nome }))}
                    aiuto="Le voci riservate si filtrano solo con l’accesso all’area riservata." />
          )}
          {puo('riservato.leggi') && (
            <Scelta etichetta="Area riservata" valore={filtro('area_riservata')} vuota="Tutte"
                    cambia={(v) => aggiornaFiltri({ area_riservata: v })} opzioni={SI_NO} />
          )}
          <Scelta etichetta="Follow up programmato" valore={filtro('follow_up')} vuota="Tutti"
                  cambia={(v) => aggiornaFiltri({ follow_up: v })} opzioni={SI_NO} />
          <Scelta etichetta="Da completare" valore={filtro('da_completare')} vuota="Tutte"
                  cambia={(v) => aggiornaFiltri({ da_completare: v })} opzioni={SI_NO}
                  aiuto="Schede a cui mancano dati obbligatori." />
          <Scelta etichetta="Da verificare" valore={filtro('da_verificare')} vuota="Tutte"
                  cambia={(v) => aggiornaFiltri({ da_verificare: v })} opzioni={SI_NO}
                  aiuto="Dal vecchio archivio, con la data di nascita o il codice fiscale da controllare." />
          <Scelta etichetta="Senza Sede Servizio" valore={filtro('senza_sede')} vuota="Tutte"
                  cambia={(v) => aggiornaFiltri({ senza_sede: v })} opzioni={SI_NO}
                  aiuto="Le vede per intero solo chi ha un incarico su tutta CIDIS." />
          <Scelta etichetta="Dati anonimizzati" valore={filtro('anonimizzate')} vuota="Tutte"
                  cambia={(v) => aggiornaFiltri({ anonimizzate: v })} opzioni={SI_NO}
                  aiuto="Persone che non possono firmare l’informativa: nome e cognome sono iniziali." />
          {(puo('ente.leggi') || puo('persona.crea') || puo('persona.modifica')) && (
            <SceltaEnte etichetta="Ente" vuota="Tutti gli enti" valore={ente} perimetroPersone={!puo('ente.leggi')}
                        cambia={(e) => { setEnte(e); aggiornaFiltri({ stakeholder: e?.id ?? '' }) }} />
          )}
        </div>
      </Riquadro>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {caricamento && <Attesa testo="Ricerca in corso…" />}

      {!caricamento && esito && (
        <Riquadro denso>
          {/* D-02: cognome e nome in colonne separate, cittadinanza e comune. Il titolo della colonna
              Comune porta una classe perche' la colonna si nasconde per prima su schermo stretto
              (stile-persona.css): Tabella non accetta classi sulle celle d'intestazione. */}
          <Tabella
            intestazioni={['Scheda', 'Cognome', 'Nome', 'Nascita', 'Cittadinanza',
              <span key="comune" className="persona-colonna-comune-titolo">Comune</span>, 'Stato', 'Aggiornata']}
            vuoto={<Vuoto testo="Nessun beneficiario trovato fra quelli che puoi vedere."
                          azione={nuovoBeneficiario} />}
          >
            {esito.risultati.map((p) => (
              <tr key={p.id}>
                <td data-etichetta="Scheda" className="mono">{p.numero_scheda}</td>
                <td data-etichetta="Cognome">
                  <Link to={`/persone/${p.id}`} className="voce-principale">{p.cognome}</Link>
                  {p.ha_area_riservata && <> <Etichetta tono="riservato">
                    <IconaLucchetto /><span className="solo-lettore">Area riservata</span>
                  </Etichetta></>}
                  {p.da_verificare && <> <Etichetta tono="attenzione">da verificare</Etichetta></>}
                  {p.dati_anonimizzati && <> <Etichetta tono="informativo">Dati anonimizzati</Etichetta></>}
                </td>
                <td data-etichetta="Nome">{p.nome}</td>
                <td data-etichetta="Nascita">
                  {p.data_nascita ? new Date(p.data_nascita).toLocaleDateString('it-IT')
                    : p.anno_nascita ?? <span className="tenue">—</span>}
                </td>
                {/* Sulle righe mascherate cittadinanza e comune non arrivano dall'API: la cella resta
                    vuota e, nella vista a schede, non si mostra. */}
                <td data-etichetta="Cittadinanza">
                  {p.mascherata ? null : p.cittadinanza_nome ?? <span className="tenue">—</span>}
                </td>
                <td data-etichetta="Comune" className="persona-colonna-comune">
                  {p.mascherata ? null : p.comune_nome
                    ? (
                      <span>
                        {p.comune_nome}{p.comune_provincia ? ` (${p.comune_provincia})` : ''}
                        {p.comune_tipo === 'domicilio' && <span className="piccolo tenue"> domicilio</span>}
                      </span>
                    )
                    : <span className="tenue">—</span>}
                </td>
                <td data-etichetta="Stato">
                  <span title={STATI_SCHEDA[p.stato]?.aiuto}>
                    <Etichetta tono={p.stato === 'completa' ? 'positivo' : p.stato === 'archiviata' ? 'neutro' : 'attenzione'}>
                      {etichettaStatoScheda(p.stato)}
                    </Etichetta>
                  </span>
                </td>
                <td data-etichetta="Aggiornata">{new Date(p.aggiornata_il).toLocaleDateString('it-IT')}</td>
              </tr>
            ))}
          </Tabella>
          {esito.risultati.some((p) => p.stato === 'bozza' || p.stato === 'storico_incompleto') && (
            <p className="nota-elenco">
              «Da completare»: mancano dati obbligatori; «vecchio archivio» indica le schede venute dal sistema
              precedente. Si completano dalla scheda, con «Completa la scheda», oppure dalla pagina{' '}
              <Link to="/da-completare">Da completare</Link>.
            </p>
          )}
          {esito.risultati.some((p) => p.mascherata) && (
            <p className="nota-elenco">
              Le righe con le iniziali sono schede mascherate: della nascita resta l’anno, cittadinanza e
              comune non si mostrano.
            </p>
          )}
          <Paginazione salta={salta} limite={LIMITE} totale={esito.totale}
                       ricevute={esito.risultati.length} cambia={setSalta} />
        </Riquadro>
      )}
      {importa && <ImportaAnonimizzate chiudi={() => setImporta(false)} importate={() => setImportate((n) => n + 1)} />}
    </>
  )
}
